import { useRouter } from 'next/router'

import ArrowButton from '~/checklist/components/ArrowButton'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'

import { CurriculumEvents } from '~/analytics/curriculum/curriculumEvents.types'
import curriculumAnalytics from '~/analytics/curriculum/curriculumAnalytics'
import ROUTES from '~/shared/constants/routes.const'

import {
  selectGradePageCurriculumDropdownExpanded,
  selectMenuCurriculumDropdownExpanded,
  setGradePageCurriculumDropdownExpandedActionCreator,
  setMenuCurriculumDropdownExpandedActionCreator,
} from '../slice/curriculum.slice'
import { useGetInCurriculumV2 } from '../hooks/useGetCurriculumCountryCode'
import { getCurriculumDisplayLabel } from '../constants/usCurriculumStates.const'

import CurriculumDropdownItem from './CurriculumDropdownItem'
import CurriculumDropdownOptions from './CurriculumDropdownOptions'

interface Props {
  isForMenu?: boolean
  handleOptionSelect: (chosenCurriculumName: string) => void
  allowOverflow?: boolean
  currentCurriculum: string
  currentCurriculumCode: string
  unsetStateLabel?: string
  theme?: 'light' | 'dark'
  isLightNested?: boolean
}

export default function CurriculumDropdown({
  isForMenu,
  allowOverflow,
  handleOptionSelect,
  currentCurriculum,
  currentCurriculumCode,
  unsetStateLabel,
  theme = 'light',
  isLightNested,
}: Props): React.ReactElement {
  const isDark = theme === 'dark'
  const isExpandedFromMenu = useAppSelector(selectMenuCurriculumDropdownExpanded)
  const isExpandedFromGradePage = useAppSelector(selectGradePageCurriculumDropdownExpanded)
  const isExpanded = isForMenu ? isExpandedFromMenu : isExpandedFromGradePage
  const dispatch = useAppDispatch()
  const isCurriculumV2 = useGetInCurriculumV2()
  const router = useRouter()
  const isOnboarding = router.pathname.includes(ROUTES.ONBOARDING)

  const isPanelAttached = isDark || Boolean(isLightNested)

  const expansionHandler = (): void => {
    const nextExpanded = !isExpanded
    curriculumAnalytics.logCurriculumClick(
      CurriculumEvents.CURRICULUM_COUNTRY_SELECTOR_TOGGLED,
      {
        surface: isForMenu ? 'menu' : 'select_grade',
        is_expanded: nextExpanded,
      },
      isCurriculumV2 ? 'v2' : 'v1',
    )
    if (isForMenu) {
      dispatch(setMenuCurriculumDropdownExpandedActionCreator(nextExpanded))
    } else {
      dispatch(setGradePageCurriculumDropdownExpandedActionCreator(nextExpanded))
    }
  }

  return (
    <div
      className={`flex flex-col relative ${
        // w-84 (336px) is what the longest label needs on one line: 8px padding + 32px flag + 10px gap +
        // 249px for "California - CA CCSSM" at 20px/500 Poppins + 20px arrow + 8px padding = 327px, plus a
        // little air before the arrow. Any narrower and the label wrapped to a second line, which the
        // fixed h-11 then pushed under the panel. The panel matches, since it inherits this width.
        isLightNested ? 'w-84 h-11' : isOnboarding ? 'min-w-57 h-11' : isDark ? 'w-67' : 'w-46'
      } items-start ${isPanelAttached ? '' : 'space-y-1.5'}`}
    >
      <div
        className={`w-full flex justify-between items-center ${
          isPanelAttached && isExpanded ? 'rounded-t-2' : 'rounded-2'
        } ${isOnboarding || isLightNested ? 'h-full whitespace-nowrap' : ''} ${
          isDark
            ? 'px-3 py-2 border border-blue-periwinkle-soft text-white'
            : `pl-2 pr-2 py-2 bg-white/70 ${isForMenu ? 'border-gray-placeholder border' : ''}`
        }`}
        onClick={expansionHandler}
      >
        <CurriculumDropdownItem
          curriculumCountryCode={currentCurriculumCode}
          curriculumName={
            unsetStateLabel ??
            (isDark || isLightNested ? getCurriculumDisplayLabel(currentCurriculum) : currentCurriculum)
          }
          isDark={isDark}
          isMenu={isForMenu}
          isOnboarding={isOnboarding}
          isUnset={Boolean(unsetStateLabel)}
          textClassName={isDark ? 'text-xs font-normal' : undefined}
        />
        <ArrowButton color={isDark ? 'text-white' : 'text-black'} expanded={isExpanded} size={20} />
      </div>
      <div
        className={`z-20 overflow-hidden ${
          allowOverflow ? `absolute top-full ${isDark ? '-mt-px' : isPanelAttached ? '' : 'mt-1.5'}` : ''
        }  w-full ${
          isDark
            ? 'bg-purple-abyss text-white border-solid border-white-fade rounded-b-2.5'
            : `drop-shadow-2xl bg-white ${isForMenu ? 'shadow' : 'border-purple'} ${
                isPanelAttached ? 'rounded-b-2' : 'rounded-2'
              }`
        }`}
        style={{
          transition: `max-height 400ms cubic-bezier( 0.4, 0, 1, 1), border-width 400ms cubic-bezier( 1, 1, 1, 1)`,
          maxHeight: isExpanded ? (isLightNested ? '550px' : isOnboarding ? '326px' : isDark ? '550px' : '200px') : 0,
          borderWidth: isExpanded ? (isForMenu ? (isDark ? '1px' : '0px') : '2px') : '0px',
        }}
      >
        <CurriculumDropdownOptions
          currentCurriculum={currentCurriculum}
          currentCurriculumCode={currentCurriculumCode}
          handleOptionSelect={handleOptionSelect}
          isDark={isDark}
          isLightNested={isLightNested}
          isMenu={isForMenu}
          isOnboarding={isOnboarding}
        />
      </div>
    </div>
  )
}
